import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Send, MessageSquare, Inbox, Shield, Zap, LogOut, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/demo`;

function authHeaders() {
  const token = localStorage.getItem('wa_demo_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function api(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail?.error?.message || data?.detail || `HTTP ${res.status}`);
  return data;
}

export function WhatsAppDemoLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('reviewer@wamerce.com');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('wa_demo_token')) navigate('/whatsapp/dashboard');
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Login failed');
      localStorage.setItem('wa_demo_token', data.token);
      localStorage.setItem('wa_demo_user', JSON.stringify(data.user));
      toast.success('Signed in');
      navigate('/whatsapp/dashboard');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-200 flex flex-col">
      <header className="border-b border-zinc-800/60">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold tracking-tight text-white">
            Wamerce
          </Link>
          <span className="text-xs uppercase tracking-[0.18em] text-emerald-400">
            WhatsApp Console
          </span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md space-y-10">
          <div className="space-y-3 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
              <MessageSquare className="w-6 h-6" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
              WhatsApp Business Console
            </h1>
            <p className="text-sm text-zinc-400">
              Sign in to send messages, view inbox & manage automations on your WABA.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="wa-login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">
                Work email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                data-testid="wa-login-email"
                className="bg-zinc-900/60 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-emerald-500/40"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="wa-login-password"
                className="bg-zinc-900/60 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-emerald-500/40"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="wa-login-submit"
              className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-medium"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>

          <p className="text-xs text-zinc-500 text-center leading-relaxed">
            By signing in you agree to our{' '}
            <Link to="/privacy-policy" className="text-emerald-400 hover:underline">
              Privacy Policy
            </Link>
            . Sessions expire after 8 hours.
          </p>
        </div>
      </main>

      <footer className="border-t border-zinc-800/60 py-4">
        <div className="max-w-6xl mx-auto px-6 text-xs text-zinc-500 text-center">
          © {new Date().getFullYear()} Wamerce · WhatsApp Business Console
        </div>
      </footer>
    </div>
  );
}

export function WhatsAppDemoDashboard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);

  const [to, setTo] = useState('+91');
  const [tplName, setTplName] = useState('');
  const [langCode, setLangCode] = useState('en_US');
  const [vars, setVars] = useState('');
  const [textBody, setTextBody] = useState('Hello from Wamerce — this is a test message.');
  const [sending, setSending] = useState(false);

  const logout = () => {
    localStorage.removeItem('wa_demo_token');
    localStorage.removeItem('wa_demo_user');
    navigate('/whatsapp/login');
  };

  const load = async () => {
    try {
      const [s, t, m, a] = await Promise.all([
        api('/status'),
        api('/templates'),
        api('/messages'),
        api('/automations'),
      ]);
      setStatus(s);
      setTemplates(t.templates || []);
      setMessages(m.messages || []);
      setAutomations(a.automations || []);
    } catch (e) {
      if (`${e.message}`.toLowerCase().includes('token') || `${e.message}`.includes('401')) {
        logout();
        return;
      }
      toast.error(e.message);
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('wa_demo_token')) {
      navigate('/whatsapp/login');
      return;
    }
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const sendMessage = async () => {
    setSending(true);
    try {
      const body = {
        to,
        language_code: langCode,
        body_variables: vars
          ? vars
              .split('|')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      };
      if (tplName) body.template_name = tplName;
      else body.text_body = textBody;
      const res = await api('/send', { method: 'POST', body: JSON.stringify(body) });
      toast.success(`Sent · wamid ${res.message_id?.slice(-10)}`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  if (loadingPage) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-200">
      <header className="border-b border-zinc-800/60 bg-[#0a0a0a]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-lg font-semibold text-white">
              Wamerce
            </Link>
            <span className="text-xs uppercase tracking-[0.18em] text-emerald-400 border-l border-zinc-800 pl-3">
              WhatsApp Console
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            data-testid="wa-logout-btn"
            className="text-zinc-400 hover:text-white hover:bg-zinc-900"
          >
            <LogOut className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-10">
        <StatusStrip status={status} />

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
          <SendPanel
            templates={templates}
            to={to}
            setTo={setTo}
            tplName={tplName}
            setTplName={setTplName}
            langCode={langCode}
            setLangCode={setLangCode}
            vars={vars}
            setVars={setVars}
            textBody={textBody}
            setTextBody={setTextBody}
            sending={sending}
            send={sendMessage}
          />
          <InboxPanel messages={messages} />
        </div>

        <AutomationsPanel items={automations} />
        <TemplatesPanel templates={templates} />
      </main>
    </div>
  );
}

function StatusStrip({ status }) {
  const ok = status?.configured;
  return (
    <section
      data-testid="wa-status-strip"
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 flex flex-wrap items-center gap-6"
    >
      <div className="flex items-center gap-3">
        <span
          className={`w-2.5 h-2.5 rounded-full ${
            ok ? 'bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.6)]' : 'bg-amber-400'
          }`}
        />
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500">WABA Status</p>
          <p className="text-sm text-white font-medium">
            {ok ? 'Connected to Meta Cloud API' : 'Awaiting access token'}
          </p>
        </div>
      </div>
      <Stat label="Phone" value={status?.phone || '—'} />
      <Stat label="Display name" value={status?.display_name || '—'} />
      <Stat label="Quality" value={status?.quality_rating || '—'} />
      <Stat label="Tier" value={status?.messaging_limit || '—'} />
      <Stat label="WABA ID" value={status?.waba_id ? `…${status.waba_id.slice(-6)}` : '—'} mono />
      <Stat
        label="Phone ID"
        value={status?.phone_number_id ? `…${status.phone_number_id.slice(-6)}` : '—'}
        mono
      />
    </section>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="min-w-[100px]">
      <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-sm text-white ${mono ? 'font-mono' : 'font-medium'}`}>{value}</p>
    </div>
  );
}

function SendPanel(props) {
  const {
    templates,
    to,
    setTo,
    tplName,
    setTplName,
    langCode,
    setLangCode,
    vars,
    setVars,
    textBody,
    setTextBody,
    sending,
    send,
  } = props;
  const approved = templates.filter((t) => t.status === 'APPROVED');

  return (
    <section
      data-testid="wa-send-panel"
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-5"
    >
      <header className="flex items-center gap-2">
        <Send className="w-4 h-4 text-emerald-400" />
        <h2 className="text-base font-semibold text-white">Send a message</h2>
      </header>

      <div className="space-y-2">
        <Label className="text-zinc-300">Recipient (E.164)</Label>
        <Input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="+918962778092"
          data-testid="wa-send-to"
          className="bg-zinc-950/60 border-zinc-800 font-mono text-zinc-100"
        />
        <p className="text-xs text-zinc-500">
          During development, recipient must be a registered test number in Meta App Dashboard.
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Template</Label>
        <select
          value={tplName}
          onChange={(e) => setTplName(e.target.value)}
          data-testid="wa-send-template"
          className="w-full h-10 rounded-md bg-zinc-950/60 border border-zinc-800 text-zinc-100 px-3 text-sm"
        >
          <option value="">— Free-text (development mode only) —</option>
          {approved.map((t) => (
            <option key={`${t.name}_${t.language}`} value={t.name}>
              {t.name} · {t.language} · {t.category}
            </option>
          ))}
        </select>
      </div>

      {tplName ? (
        <>
          <div className="space-y-2">
            <Label className="text-zinc-300">Language code</Label>
            <Input
              value={langCode}
              onChange={(e) => setLangCode(e.target.value)}
              placeholder="en_US"
              data-testid="wa-send-language"
              className="bg-zinc-950/60 border-zinc-800 font-mono text-zinc-100"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Body variables (pipe-separated)</Label>
            <Input
              value={vars}
              onChange={(e) => setVars(e.target.value)}
              placeholder="Aman | #29614 | 2,499 | https://www.tnvcollection.com/orders/29614"
              data-testid="wa-send-vars"
              className="bg-zinc-950/60 border-zinc-800 text-zinc-100"
            />
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <Label className="text-zinc-300">Message body</Label>
          <textarea
            value={textBody}
            onChange={(e) => setTextBody(e.target.value)}
            rows={4}
            data-testid="wa-send-text"
            className="w-full rounded-md bg-zinc-950/60 border border-zinc-800 text-zinc-100 px-3 py-2 text-sm font-mono resize-none"
          />
        </div>
      )}

      <Button
        onClick={send}
        disabled={sending || !to}
        data-testid="wa-send-submit"
        className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-medium"
      >
        {sending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" /> Send message
          </>
        )}
      </Button>
    </section>
  );
}

function InboxPanel({ messages }) {
  return (
    <section
      data-testid="wa-inbox-panel"
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-5"
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 text-emerald-400" />
          <h2 className="text-base font-semibold text-white">Recent activity</h2>
        </div>
        <span className="text-xs text-zinc-500">Auto-refreshes every 15s</span>
      </header>
      {messages.length === 0 ? (
        <p className="text-sm text-zinc-500 italic py-12 text-center">
          No messages yet. Send your first one from the left panel.
        </p>
      ) : (
        <ul className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
          {messages.map((m, i) => (
            <li
              key={m.message_id || i}
              data-testid={`wa-msg-${i}`}
              className="rounded-xl border border-zinc-800 bg-zinc-950/40 px-4 py-3 space-y-1.5"
            >
              <div className="flex items-center justify-between text-xs">
                <span className="text-emerald-400 font-medium uppercase tracking-wider">
                  {m.direction === 'outgoing' ? '→ Sent' : '← Received'}
                </span>
                <span className="text-zinc-500 font-mono">
                  {m.sent_at ? new Date(m.sent_at * 1000).toLocaleTimeString() : '—'}
                </span>
              </div>
              <p className="text-sm text-zinc-100">
                <span className="font-mono text-zinc-400">{m.to}</span>{' '}
                {m.template_name ? (
                  <span className="text-emerald-300">· {m.template_name}</span>
                ) : (
                  <span className="text-zinc-300">· {m.text_body}</span>
                )}
              </p>
              <div className="flex items-center gap-2 text-[11px]">
                <StatusPill s={m.status} />
                <span className="text-zinc-600 font-mono">
                  {m.message_id ? `wamid…${m.message_id.slice(-10)}` : ''}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatusPill({ s }) {
  const map = {
    sent: 'bg-zinc-800 text-zinc-300',
    delivered: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    read: 'bg-emerald-500/25 text-emerald-200 border border-emerald-500/40',
    failed: 'bg-red-500/15 text-red-300 border border-red-500/30',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full uppercase tracking-wider text-[10px] font-medium ${
        map[s] || 'bg-zinc-800 text-zinc-400'
      }`}
    >
      {s || 'pending'}
    </span>
  );
}

function AutomationsPanel({ items }) {
  return (
    <section
      data-testid="wa-automations"
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-5"
    >
      <header className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-emerald-400" />
        <h2 className="text-base font-semibold text-white">Automations</h2>
      </header>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((a) => (
          <div
            key={a.id}
            data-testid={`wa-auto-${a.id}`}
            className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4 space-y-2"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">{a.name}</h3>
              <span className="text-[10px] uppercase tracking-wider text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                {a.status}
              </span>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">{a.description}</p>
            <div className="flex items-center gap-3 pt-1 text-[11px] font-mono text-zinc-500">
              <span>trigger: {a.trigger}</span>
            </div>
            <div className="flex items-center gap-3 text-[11px] font-mono text-zinc-500">
              <span>template: {a.template}</span>
              <span>·</span>
              <span>{a.category}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TemplatesPanel({ templates }) {
  return (
    <section
      data-testid="wa-templates"
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-4"
    >
      <header className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-emerald-400" />
        <h2 className="text-base font-semibold text-white">
          Approved templates ({templates.length})
        </h2>
      </header>
      {templates.length === 0 ? (
        <p className="text-sm text-zinc-500 italic">
          No templates loaded yet. They will appear here once approved in Meta WhatsApp Manager.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase tracking-wider">
              <tr className="border-b border-zinc-800">
                <th className="text-left py-2 pr-4 font-medium">Name</th>
                <th className="text-left py-2 pr-4 font-medium">Language</th>
                <th className="text-left py-2 pr-4 font-medium">Category</th>
                <th className="text-left py-2 pr-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr
                  key={`${t.name}_${t.language}`}
                  className="border-b border-zinc-800/60 last:border-0"
                >
                  <td className="py-2 pr-4 font-mono text-zinc-100">{t.name}</td>
                  <td className="py-2 pr-4 text-zinc-400">{t.language}</td>
                  <td className="py-2 pr-4 text-zinc-400">{t.category}</td>
                  <td className="py-2 pr-4">
                    <StatusPill s={(t.status || '').toLowerCase()} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default WhatsAppDemoLogin;
